import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { fetchWallet, fetchTransactions, fetchWithdrawalRequests } from "./client";
import DepositModal  from "./DepositModal";
import WithdrawModal from "./WithdrawModal";
import { useI18n } from "./i18n";
import { trackPageView } from "./uxTracker";
import { fmtAmount } from "./Utils";
import { LockedSection } from "./BottomNavBar";


const WTX = {
  title:     { fr: "Mon", en: "My", es: "Mi", de: "Mein", pt: "Minha", ar: "محفظتي" },
  title2:    { fr: "Portefeuille", en: "Wallet", es: "Cartera", de: "Wallet", pt: "Carteira", ar: "" },
  sub:       { fr: "Gérez vos EVP et vos récompenses.", en: "Manage your EVP and rewards.", es: "Administra tus EVP y recompensas.", de: "Verwalten Sie Ihre EVP und Belohnungen.", pt: "Gerencie seus EVP e recompensas.", ar: "إدارة EVP والمكافآت." },
  deposit:   { fr: "↓ Dépôt", en: "↓ Deposit", es: "↓ Depósito", de: "↓ Einzahlen", pt: "↓ Depositar", ar: "↓ إيداع" },
  withdraw:  { fr: "↑ Retirer", en: "↑ Withdraw", es: "↑ Retirar", de: "↑ Abheben", pt: "↑ Sacar", ar: "↑ سحب" },
  balance:   { fr: "Solde EVP", en: "EVP Balance", es: "Saldo EVP", de: "EVP-Guthaben", pt: "Saldo EVP", ar: "رصيد EVP" },
  balSub:    { fr: "Disponible pour trader", en: "Available to trade", es: "Disponible para operar", de: "Zum Handeln verfügbar", pt: "Disponível para negociar", ar: "متاح للتداول" },
  deposited: { fr: "Dépôts totaux", en: "Total Deposited", es: "Total depositado", de: "Gesamt eingezahlt", pt: "Total depositado", ar: "إجمالي الإيداعات" },
  withdrawn: { fr: "Retraits totaux", en: "Total Withdrawn", es: "Total retirado", de: "Gesamt abgehoben", pt: "Total sacado", ar: "إجمالي السحوبات" },
  pending:   { fr: "Retraits en attente", en: "Pending Withdrawals", es: "Retiros pendientes", de: "Ausstehende Abhebungen", pt: "Saques pendentes", ar: "السحوبات المعلقة" },
  allTimeDeposits:   { fr: "Dépôts depuis le début", en: "All-time deposits", es: "Depósitos históricos", de: "Gesamte Einzahlungen", pt: "Depósitos de todos os tempos", ar: "إجمالي الإيداعات التاريخية" },
  allTimeWithdrawals: { fr: "Retraits depuis le début", en: "All-time withdrawals", es: "Retiros históricos", de: "Gesamte Abhebungen", pt: "Saques de todos os tempos", ar: "إجمالي السحوبات التاريخية" },
  txhist:    { fr: "Historique des transactions", en: "Transaction History", es: "Historial de transacciones", de: "Transaktionsverlauf", pt: "Histórico de transações", ar: "سجل المعاملات" },
  alltime:   { fr: "Tous les temps", en: "All-time", es: "De todos los tiempos", de: "Gesamt", pt: "Total", ar: "إجمالي" },
  available: { fr: "Disponible", en: "Available", es: "Disponible", de: "Verfügbar", pt: "Disponível", ar: "متاح" },
  waiting:   { fr: "En attente de traitement", en: "Awaiting processing", es: "En espera de procesamiento", de: "Wird verarbeitet", pt: "Aguardando processamento", ar: "في انتظار المعالجة" },
  wdRequests:{ fr: "Demandes de retrait", en: "Withdrawal Requests", es: "Solicitudes de retiro", de: "Auszahlungsanträge", pt: "Solicitações de saque", ar: "طلبات السحب" },
  pendingCount: { fr: "en attente", en: "pending", es: "pendientes", de: "ausstehend", pt: "pendentes", ar: "معلق" },
  withdrawTo:   { fr: "Retirer", en: "Withdraw", es: "Retirar", de: "Abheben", pt: "Sacar", ar: "سحب" },
  noTx:      { fr: "Aucune transaction.", en: "No transactions yet.", es: "Sin transacciones aún.", de: "Noch keine Transaktionen.", pt: "Nenhuma transação ainda.", ar: "لا توجد معاملات بعد." },
  login:     { fr: "🔒 Connectez-vous pour voir votre wallet", en: "🔒 Sign in to view your wallet", es: "🔒 Inicia sesión para ver tu cartera", de: "🔒 Melde dich an für dein Wallet", pt: "🔒 Faça login para ver sua carteira", ar: "🔒 سجّل الدخول لعرض محفظتك" },
  tabs: {
    ALL:        { fr: "TOUT", en: "ALL", es: "TODO", de: "ALLE", pt: "TUDO", ar: "الكل" },
    DEPOSIT:    { fr: "DÉPÔT", en: "DEPOSIT", es: "DEPÓSITO", de: "EINZAHLUNG", pt: "DEPÓSITO", ar: "إيداع" },
    WITHDRAWAL: { fr: "RETRAIT", en: "WITHDRAWAL", es: "RETIRO", de: "AUSZAHLUNG", pt: "SAQUE", ar: "سحب" },
    TRADE:      { fr: "TRADE", en: "TRADE", es: "TRADING", de: "HANDEL", pt: "TRADE", ar: "تداول" }
  }
};
function wt(key, lang) { return WTX[key]?.[lang] ?? WTX[key]?.fr ?? key; }


/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Wrapper = styled.div`max-width:1000px;margin:0 auto;padding:2rem 2rem 4rem;`;

const PageHead = styled.div`
  display:flex;align-items:flex-end;justify-content:space-between;
  margin-bottom:2rem;flex-wrap:wrap;gap:1rem;
`;
const PageTitle = styled.h1`
  font-family:${({theme})=>theme.fontDisplay};font-size:1.8rem;font-weight:700;
  letter-spacing:-.03em;
  span{color:${({theme})=>theme.green};}
`;
const PageSub = styled.p`font-size:.78rem;color:${({theme})=>theme.textMuted};margin-top:.25rem;`;

const ActionRow = styled.div`display:flex;gap:.75rem;`;
const ActionBtn = styled.button`
  display:flex;align-items:center;gap:.4rem;
  padding:.55rem 1.1rem;border-radius:8px;
  font-family:${({theme})=>theme.fontBody};font-size:.8rem;font-weight:600;
  letter-spacing:.03em;cursor:pointer;transition:all .15s;
  border:1px solid ${({$primary,theme})=>$primary?theme.green:theme.accent};
  background:${({$primary,theme})=>$primary?theme.greenDim:"rgba(79,195,247,.08)"};
  color:${({$primary,theme})=>$primary?theme.green:theme.accent};
  &:hover{opacity:.8;}
`;

/* ─── Balance cards ──────────────────────────────────────────────────────── */
const CardGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:1rem;
  margin-bottom:2rem;
`;
const BalCard = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1.1rem 1.25rem;
  animation:${fadeUp} .4s ease both;animation-delay:${({$i})=>$i*.07}s;
  position:relative;overflow:hidden;
  &::after{
    content:'';position:absolute;inset:0;
    background:${({$glow,theme})=>$glow?`radial-gradient(ellipse 60% 60% at 50% -10%, ${theme.green}18, transparent 70%)`:"none"};
    pointer-events:none;
  }
`;
const CardLabel = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};
  margin-bottom:.4rem;
`;
const CardValue = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:1.3rem;font-weight:500;
  color:${({$green,$accent,theme})=>$green?theme.green:$accent?theme.accent:theme.text};
`;
const CardSub = styled.div`font-size:.68rem;color:${({theme})=>theme.textMuted};margin-top:.25rem;`;

/* ─── History section ────────────────────────────────────────────────────── */
const Section = styled.div`
  background:${({theme})=>theme.bgCard};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;overflow:hidden;margin-bottom:1.25rem;
`;
const SectionHead = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:.9rem 1.25rem;background:${({theme})=>theme.bg};
  border-bottom:1px solid ${({theme})=>theme.border};
`;
const SectionTitle = styled.h3`
  font-family:${({theme})=>theme.fontMono};font-size:.68rem;
  text-transform:uppercase;letter-spacing:.08em;color:${({theme})=>theme.textMuted};
`;
const TabRow = styled.div`display:flex;gap:.3rem;@media(max-width:600px){grid-template-columns:1fr 1fr; display:grid};`;
const TabBtn = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;padding:.25rem .65rem;
  border-radius:100px;
  border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};
  background:${({$active,theme})=>$active?theme.bgHover:"transparent"};
  color:${({$active,theme})=>$active?theme.text:theme.textMuted};
  cursor:pointer;letter-spacing:.05em;transition:all .15s;
  &:hover{border-color:${({theme})=>theme.borderLight};}
`;

const TxList = styled.div``;
const TxRow = styled.div`
  display:grid;grid-template-columns:36px 1fr auto auto auto;
  align-items:center;gap:.75rem;
  padding:.9rem 1.25rem;border-bottom:1px solid ${({theme})=>theme.border};
  transition:background .15s;
  &:last-child{border-bottom:none;}
  &:hover{background:${({theme})=>theme.bgHover};}
  @media(max-width:600px){grid-template-columns:36px 1fr auto;}
`;
const TxIcon = styled.div`
  width:36px;height:36px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;font-size:.9rem;flex-shrink:0;
  background:${({$type,theme})=>
    $type==="deposit"   ? theme.greenDim :
    $type==="withdrawal"? "rgba(79,195,247,.1)" :
    $type==="trade"     ? theme.redDim   : theme.bgHover};
`;
const TxInfo = styled.div`display:flex;flex-direction:column;gap:.15rem;`;
const TxLabel = styled.span`font-size:.8rem;font-weight:500;`;
const TxDate  = styled.span`font-size:.65rem;color:${({theme})=>theme.textMuted};font-family:${({theme})=>theme.fontMono};`;
const TxAmount = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.82rem;font-weight:500;
  color:${({$in,theme})=>$in?theme.green:theme.text};
  white-space:nowrap;
`;
const TxCoin = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.65rem;
  color:${({theme})=>theme.textMuted};white-space:nowrap;
  @media(max-width:600px){display:none;}
`;
const StatusDot = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;padding:.18rem .5rem;
  border-radius:100px;letter-spacing:.05em;
  background:${({$s,theme})=>
    $s==="CONFIRMED"||$s==="COMPLETED" ? theme.greenDim :
    $s==="PENDING"  ? "rgba(255,193,7,.1)"                :
    $s==="REJECTED" ? theme.redDim                        : theme.bgHover};
  color:${({$s,theme})=>
    $s==="CONFIRMED"||$s==="COMPLETED" ? theme.green  :
    $s==="PENDING"  ? theme.gold                          :
    $s==="REJECTED" ? theme.red                           : theme.textMuted};
  @media(max-width:600px){display:none;}
`;

const Empty = styled.div`
  text-align:center;padding:2.5rem;
  font-size:.8rem;color:${({theme})=>theme.textMuted};
`;
const Skeleton = styled.div`
  height:54px;margin:.4rem 1.25rem;border-radius:6px;
  background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);
  background-size:400px 100%;animation:${shimmer} 1.4s infinite linear;
`;

/* ─── Mock data ───────────────────────────────────────────────────────────── */
const MOCK_WALLET = { usdc_balance:"1000.00", total_deposited:"1200.00", total_withdrawn:"200.00", pending_withdrawals:"0.00" };
const MOCK_TXS = [
  { id:1, type:"deposit",    label:"USDC Deposit",         coin:"USDC", amount:"+500.00",  status:"CONFIRMED", date:"2026-03-12T10:22:00Z" },
  { id:2, type:"deposit",    label:"ETH Deposit",          coin:"ETH",  amount:"+300.00",  status:"CONFIRMED", date:"2026-03-08T14:05:00Z" },
  { id:3, type:"trade",      label:"Bought — Yes / BTC 100K",coin:"USDC",amount:"-32.50", status:"COMPLETED", date:"2026-03-07T09:11:00Z" },
  { id:4, type:"withdrawal", label:"Withdrawal to 0x3f5C…",coin:"USDC", amount:"-200.00", status:"PENDING",   date:"2026-03-05T18:40:00Z" },
  { id:5, type:"deposit",    label:"BTC Deposit",          coin:"BTC",  amount:"+400.00",  status:"CONFIRMED", date:"2026-02-28T08:33:00Z" },
  { id:6, type:"trade",      label:"Bought — No / GPT-5",  coin:"USDC", amount:"-11.60",  status:"COMPLETED", date:"2026-02-25T11:00:00Z" },
];
const MOCK_WITHDRAWALS = [
  { id:1, coin:"USDC", amount:"200.00", address:"0x3f5CE5FBFe3E9af3971dD833D26bA9b5C936f0bE", status:"PENDING",   created_at:"2026-03-05T18:40:00Z" },
];



function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
}
function truncAddr(a="") { return a.length > 16 ? `${a.slice(0,8)}…${a.slice(-6)}` : a; }

const TX_ICONS = { deposit:"↓", withdrawal:"↑", trade:"⇌" };

export default function WalletPage({ user, onLoginClick }) {
  const { lang } = useI18n();
  const [wallet,      setWallet]      = useState(null);
  const [txs,         setTxs]         = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [txFilter,    setTxFilter]    = useState("ALL");
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw,setShowWithdraw]= useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, t, wd] = await Promise.all([
        fetchWallet(), fetchTransactions(), fetchWithdrawalRequests()
      ]);
      setWallet(w.data); setTxs(t.data.results||t.data); setWithdrawals(wd.data.results||wd.data);
    } catch {
      setWallet(MOCK_WALLET); setTxs(MOCK_TXS); setWithdrawals(MOCK_WITHDRAWALS);
    } finally {
      setLoading(false);
    }
  }, []);
useEffect(() => { showDeposit && trackPageView("showDeposit"); showWithdraw && trackPageView("showWithdraw"); }, [showDeposit, showWithdraw]);

  useEffect(() => { load(); }, [load]);

  /*
  if (!user) return (
    <Wrapper>
      <PageTitle>{wt("title",lang)} <span>{wt("title2",lang)}</span></PageTitle>
      <Empty style={{marginTop:"3rem"}}>{wt("login",lang)}</Empty>
    </Wrapper>
  );
*/
  const balance  = fmtAmount(wallet?.usdc_balance || 0);
  const filtered = txFilter === "ALL" ? txs
    : txs.filter(t => t.type?.toUpperCase() === txFilter);

return (
  <div dir={lang === 'ar' ? 'rtl' : 'ltr'}>
    {!user ? (
      <LockedSection sectionKey="wallet" lang={lang} onSignIn={() => onLoginClick()} variant="fullscreen" />
    ) : (
      <Wrapper>
        <PageHead>
          <div>
            <PageTitle>{wt("title", lang)} <span>{wt("title2", lang)}</span></PageTitle>
            <PageSub>{wt("sub", lang)}</PageSub>
          </div>
          <ActionRow>
            <ActionBtn $primary onClick={() => setShowDeposit(true)}>
              {wt("deposit", lang)}
            </ActionBtn>
            <ActionBtn onClick={() => setShowWithdraw(true)}>
              {wt("withdraw", lang)}
            </ActionBtn>
          </ActionRow>
        </PageHead>

        {/* Balance cards */}
        <CardGrid>
          <BalCard $i={0}$glow>
            <CardLabel>{wt("balance", lang)}</CardLabel>
            <CardValue $green>{fmtAmount(balance)} EVP</CardValue>
            <CardSub>{wt("balSub", lang)}</CardSub>
          </BalCard>
          <BalCard $i={1}>
            <CardLabel>{wt("deposited", lang)}</CardLabel>
            <CardValue>{fmtAmount(wallet?.total_deposited || 0)} EVP</CardValue>
            <CardSub>{wt("allTimeDeposits", lang)}</CardSub>
          </BalCard>
          <BalCard $i={2}>
            <CardLabel>{wt("withdrawn", lang)}</CardLabel>
            <CardValue>{fmtAmount(wallet?.total_withdrawn || 0)} EVP</CardValue>
            <CardSub>{wt("allTimeWithdrawals", lang)}</CardSub>
          </BalCard>
          <BalCard $i={3}>
            <CardLabel>{wt("pending", lang)}</CardLabel>
            <CardValue $accent>{fmtAmount(wallet?.pending_withdrawals || 0)} EVP</CardValue>
            <CardSub>{wt("waiting", lang)}</CardSub>
          </BalCard>
        </CardGrid>

        {/* Transactions */}
        <Section>
          <SectionHead>
            <SectionTitle>{wt("txhist", lang)}</SectionTitle>
            <TabRow>
              {["ALL", "DEPOSIT", "WITHDRAWAL", "TRADE"].map((f) => (
                <TabBtn key={f} $active={txFilter === f} onClick={() => setTxFilter(f)}>
                  {WTX.tabs[f][lang] || f}
                </TabBtn>
              ))}
            </TabRow>
          </SectionHead>
          <TxList>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)
            ) : filtered.length === 0 ? (
              <Empty>{wt("noTx", lang)}</Empty>
            ) : (
              filtered.map((tx) => (
                <TxRow key={tx.id}>
                  <TxIcon $type={tx.type}>{TX_ICONS[tx.type] || "·"}</TxIcon>
                  <TxInfo>
                    <TxLabel>{tx.label || `${tx.type} — ${tx.coin}`}</TxLabel>
                    <TxDate>{fmt(tx.date || tx.created_at)}</TxDate>
                  </TxInfo>
                  <TxCoin>{tx.coin}</TxCoin>
                  <TxAmount $in={tx.amount?.startsWith("+") || tx.type === "deposit"}>
                    {fmtAmount(tx.amount)}
                  </TxAmount>
                  <StatusDot $s={tx.status}>{tx.status}</StatusDot>
                </TxRow>
              ))
            )}
          </TxList>
        </Section>

        {/* Pending withdrawal requests */}
        {withdrawals.length > 0 && (
          <Section>
            <SectionHead>
              <SectionTitle>{wt("wdRequests", lang)}</SectionTitle>
              <TabBtn $active>{withdrawals.length} {wt("pendingCount", lang)}</TabBtn>
            </SectionHead>
            <TxList>
              {withdrawals.map((wd) => (
                <TxRow key={wd.id}>
                  <TxIcon $type="withdrawal">↑</TxIcon>
                  <TxInfo>
                    <TxLabel>{wt("withdrawTo", lang)} {wd.coin} → {truncAddr(wd.address)}</TxLabel>
                    <TxDate>{fmt(wd.created_at)}</TxDate>
                  </TxInfo>
                  <TxCoin>{wd.coin}</TxCoin>
                  <TxAmount>{fmtAmount(wd.amount)} EVP</TxAmount>
                  <StatusDot $s={wd.status}>{wd.status}</StatusDot>
                </TxRow>
              ))}
            </TxList>
          </Section>
        )}

        {/* Modals */}
        {showDeposit && <DepositModal onClose={() => setShowDeposit(false)} />}
        {showWithdraw && (
          <WithdrawModal
            balance={balance}
            onClose={() => setShowWithdraw(false)}
            onSuccess={() => {
              setShowWithdraw(false);
              load();
            }}
          />
        )}
      </Wrapper>
    )}
  </div>
);
}
